import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Badge, Button, Form, Spinner } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { shiftAPI } from '../../services/shift';
import { useBranch, useFilterParams } from '../../hooks';
import DateRangePicker from '../../components/fields/DateRangePicker';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';
import { format } from 'date-fns';

const safeFormat = (dateStr, fmt) => {
  try {
    if (!dateStr) return '--';
    return format(new Date(dateStr), fmt);
  } catch {
    return '--';
  }
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('vi-VN').format(amount || 0) + 'đ';

/**
 * #000744 — doanh thu gom theo CA, không theo ngày lịch. Một ca vắt qua nửa đêm
 * (vd 20:00 hôm nay → 01:00 hôm sau) là MỘT ca duy nhất và thuộc trọn về NGÀY
 * CA BẮT ĐẦU; mọi đơn của nó — kể cả đơn thanh toán sau 00:00 — nằm ở ngày đó.
 *
 * Quản lý nhìn cột "Doanh thu" cần biết ngay điều này, nếu không sẽ tưởng ngày
 * hôm sau bị thiếu tiền hoặc ca này bị cộng nhầm. Trả về nhãn ngày kết thúc để
 * hiện kèm khi ca kết thúc sang ngày khác.
 */
const getShiftDaySpan = (shift) => {
  const startYmd = safeFormat(shift.start_time, 'dd/MM/yyyy');
  const endYmd = safeFormat(shift.end_time, 'dd/MM/yyyy');
  return {
    startYmd,
    endYmd,
    crossesMidnight: startYmd !== '--' && endYmd !== '--' && startYmd !== endYmd,
  };
};

const STATUS_CONFIG = {
  COMPLETED: { label: 'Hoàn thành', variant: 'success' },
  COMPLETED_WAIT_APPROVE: { label: 'Chờ duyệt', variant: 'warning' },
  APPROVED: { label: 'Đã duyệt', variant: 'info' },
  REJECTED: { label: 'Từ chối', variant: 'danger' },
  // #190609 — xem chú thích ở ShiftHeader.js.
  PROPOSED_BOT: { label: 'Bot đề xuất', variant: 'warning' },
  APPROVE_BOT: { label: 'Đã duyệt (bot cũ)', variant: 'success' },
};

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Tất cả trạng thái' },
  { value: 'COMPLETED_WAIT_APPROVE', label: 'Chờ duyệt' },
  { value: 'COMPLETED', label: 'Hoàn thành' },
  { value: 'APPROVED', label: 'Đã duyệt' },
  { value: 'REJECTED', label: 'Từ chối' },
];

/**
 * #191439 — Màn này là ĐÍCH của link sâu trong 3 tin cảnh báo tiền/NVL (dòng 7, 8, 9 của bảng
 * khảo sát #132320): lệch tiền đầu ca, lệch tiền cuối ca, lệch NVL kiểm ca. Tin gửi kèm
 * `?branch_id=&from=&to=` nên trang PHẢI đọc được `from`/`to` từ URL, nếu không thì link mở
 * ra danh sách chưa lọc và người nhận vẫn phải tự chọn ngày — tức luật link sâu coi như trượt.
 *
 * Tên tham số lấy nguyên ở `KHUON-CHUAN-URL-PARAM.md` mục 1: khoảng ngày là `from`/`to` dạng
 * `YYYY-MM-DD` (bộ chuẩn KHÔNG có tên `date`). `branch_id` do `BranchContext` lo, không khai ở đây.
 */
const isIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v);

const SHIFT_REVIEW_FILTER_SPEC = {
  status: {
    default: 'COMPLETED,COMPLETED_WAIT_APPROVE',
    parse: (v) => v.toUpperCase(),
    valid: (v) =>
      v
        .toUpperCase()
        .split(',')
        .every((one) => Object.keys(STATUS_CONFIG).includes(one)),
  },
  from: { default: '', valid: isIsoDate },
  to: { default: '', valid: isIsoDate },
  page: { default: 1, parse: Number, valid: (v) => /^\d+$/.test(v) && Number(v) >= 1 },
  limit: { default: 20, parse: Number, valid: (v) => ['10', '20', '50'].includes(v) },
};

const ShiftReviewListPage = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();

  const [shifts, setShifts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [urlFilters, setUrlFilters, , { ignored, dismissIgnored }] =
    useFilterParams(SHIFT_REVIEW_FILTER_SPEC);

  // Giữ nguyên hình dạng `filters`/`page`/`limit` mà phần dưới của trang đang dùng, để chỉ
  // đổi NGUỒN của trạng thái chứ không phải viết lại cả trang.
  // BẮT BUỘC `useMemo`: `fetchData` là `useCallback` phụ thuộc `filters`. Nếu dựng object mới
  // mỗi lần render thì `fetchData` đổi tham chiếu mỗi render → `useEffect` gọi lại → render
  // lại → vòng lặp gọi API vô tận.
  const filters = useMemo(
    () => ({
      status: urlFilters.status,
      startDate: urlFilters.from,
      endDate: urlFilters.to,
    }),
    [urlFilters],
  );
  const page = urlFilters.page;
  const limit = urlFilters.limit;
  const setPage = (next) =>
    setUrlFilters((prev) => ({
      page: typeof next === 'function' ? next(prev.page) : next,
    }));
  const setLimit = (next) => setUrlFilters({ limit: next });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        branch_id: selectedBranch?.id,
        status: filters.status,
        date_from: filters.startDate,
        date_to: filters.endDate,
        page,
        limit,
      };
      const result = await shiftAPI.getShiftReviewList(params);
      setShifts(result.data || []);
      setSummary(result.summary || null);
      setMeta(result.meta || null);
    } catch (err) {
      console.error('Error fetching shift review list', err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, limit, selectedBranch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // `useFilterParams` tự đưa `page` về 1 khi đổi bộ lọc khác (luật 4 của hook).
  const handleFilterChange = (key, value) => {
    const urlName = { status: 'status', startDate: 'from', endDate: 'to' }[key];
    setUrlFilters({ [urlName]: value });
  };

  const getAttendanceInfo = (shift) => {
    const att = shift.attendance;
    if (!att) return { checkIn: '--', checkOut: '--', hours: '--' };
    return {
      checkIn: safeFormat(att.check_in_time, 'HH:mm'),
      checkOut: safeFormat(att.check_out_time, 'HH:mm'),
      hours: att.actual_hours
        ? `${Math.floor(att.actual_hours)}h${String(Math.round((att.actual_hours % 1) * 60)).padStart(2, '0')}m`
        : '--',
    };
  };

  return (
    <PageLayout>
      <div className='mc-page'>
        {/* Filter Card */}
        <Card className='shadow-sm border-0 mb-4'>
          <Card.Body className='pb-2'>
            <InvalidParamsNotice
              ignored={ignored}
              onDismiss={dismissIgnored}
            />
            <DateRangePicker
              fromDate={filters.startDate}
              toDate={filters.endDate}
              defaultPreset='today'
              onChange={(from, to) => {
                setUrlFilters({ from: from || '', to: to || '' });
              }}
            />
            <div className='row g-2 align-items-end mt-2'>
              <div className='col-md-4'>
                <Form.Label className='small text-muted mb-1'>Trạng thái</Form.Label>
                <Form.Select
                  size='sm'
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  {STATUS_FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </div>
              <div className='col-md-4 d-flex justify-content-end'>
                <Button
                  variant='outline-secondary'
                  size='sm'
                  onClick={() => {
                    setUrlFilters({
                      status: 'COMPLETED,COMPLETED_WAIT_APPROVE',
                      from: '',
                      to: '',
                    });
                  }}
                >
                  <i className='icofont-refresh me-1'></i> Reset
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Summary Cards */}
        {summary && (
          <div className='row g-3 mb-4'>
            <div className='col-6 col-md-3'>
              <Card className='shadow-sm border-0 h-100 border-start border-warning border-3'>
                <Card.Body className='d-flex align-items-center gap-3'>
                  <div className='rounded bg-warning bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                    <i className='icofont-clock-time text-warning fs-4'></i>
                  </div>
                  <div>
                    <div className='fw-bold fs-4'>{summary.by_status?.COMPLETED_WAIT_APPROVE || 0}</div>
                    <div className='text-muted small'>Chờ duyệt</div>
                  </div>
                </Card.Body>
              </Card>
            </div>
            <div className='col-6 col-md-3'>
              <Card className='shadow-sm border-0 h-100 border-start border-success border-3'>
                <Card.Body className='d-flex align-items-center gap-3'>
                  <div className='rounded bg-success bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                    <i className='icofont-check-circled text-success fs-4'></i>
                  </div>
                  <div>
                    <div className='fw-bold fs-4'>
                      {(summary.by_status?.APPROVED || 0) + (summary.by_status?.COMPLETED || 0)}
                    </div>
                    <div className='text-muted small'>Đã duyệt / Hoàn thành</div>
                  </div>
                </Card.Body>
              </Card>
            </div>
            <div className='col-6 col-md-3'>
              <Card className='shadow-sm border-0 h-100 border-start border-danger border-3'>
                <Card.Body className='d-flex align-items-center gap-3'>
                  <div className='rounded bg-danger bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                    <i className='icofont-close-circled text-danger fs-4'></i>
                  </div>
                  <div>
                    <div className='fw-bold fs-4'>{summary.by_status?.REJECTED || 0}</div>
                    <div className='text-muted small'>Từ chối</div>
                  </div>
                </Card.Body>
              </Card>
            </div>
            <div className='col-6 col-md-3'>
              <Card className='shadow-sm border-0 h-100 border-start border-primary border-3'>
                <Card.Body className='d-flex align-items-center gap-3'>
                  <div className='rounded bg-primary bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                    <i className='icofont-list text-primary fs-4'></i>
                  </div>
                  <div>
                    <div className='fw-bold fs-4'>{summary.total || 0}</div>
                    <div className='text-muted small'>Tổng ca</div>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </div>
        )}

        {/* Shift Table */}
        <Card className='shadow-sm border-0'>
          <Card.Body className='p-0'>
            {loading ? (
              <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 200 }}>
                <Spinner animation='border' variant='primary' />
              </div>
            ) : shifts.length === 0 ? (
              <div className='text-center py-5 text-muted'>
                <i className='icofont-ui-search fs-1 d-block mb-2'></i>
                Không có ca nào phù hợp bộ lọc
              </div>
            ) : (
              <>
                <Table responsive hover className='mb-0 align-middle'>
                  <thead className='bg-light'>
                    <tr>
                      <th className='border-bottom-0 ps-4' style={{ width: 50 }}>#</th>
                      <th className='border-bottom-0'>Nhân viên</th>
                      <th className='border-bottom-0'>Chi nhánh</th>
                      <th className='border-bottom-0'>Ca / Ngày</th>
                      <th className='border-bottom-0'>Check-in/out</th>
                      <th className='border-bottom-0'>Tasks</th>
                      <th className='border-bottom-0'>Đơn hàng</th>
                      <th className='border-bottom-0 text-end' title='Doanh thu của CA (đơn xác nhận trong ca), không phải của ngày lịch. Ca vắt nửa đêm tính trọn cho ngày ca bắt đầu.'>
                        Doanh thu ca
                      </th>
                      <th className='border-bottom-0'>Trạng thái</th>
                      <th className='border-bottom-0 pe-4 text-end' style={{ width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {shifts.map((shift, idx) => {
                      const statusCfg = STATUS_CONFIG[shift.status] || { label: shift.status, variant: 'secondary' };
                      const att = getAttendanceInfo(shift);
                      const counts = shift._counts || {};
                      const daySpan = getShiftDaySpan(shift);

                      return (
                        <tr
                          key={shift.id}
                          onClick={() => navigate(`/shifts/${shift.id}/review`)}
                          style={{ cursor: 'pointer' }}
                          className='hover-bg-light'
                        >
                          <td className='ps-4 text-muted small'>{(page - 1) * limit + idx + 1}</td>
                          <td>
                            <div className='d-flex align-items-center gap-2'>
                              <div
                                className='rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold'
                                style={{ width: 32, height: 32, fontSize: '0.75rem' }}
                              >
                                {(shift.employee?.full_name || '?').charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className='fw-medium small'>{shift.employee?.full_name || '--'}</div>
                                <div className='d-flex gap-1'>
                                  {shift.is_cashier && <Badge bg='secondary' className='x-small'>Thu ngân</Badge>}
                                  {shift.is_overtime && !shift.is_training && <Badge bg='warning' text='dark' className='x-small'>Tăng ca</Badge>}
                                  {shift.is_training && <Badge bg='primary' className='x-small'>Training</Badge>}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className='small text-muted'>{shift.branch?.name || '--'}</td>
                          <td>
                            <div className='small fw-medium'>{shift.template?.name || 'Tùy chỉnh'}</div>
                            <div className='small text-muted'>
                              {daySpan.startYmd}
                              {' '}
                              <span className='text-nowrap'>
                                {safeFormat(shift.start_time, 'HH:mm')}–{safeFormat(shift.end_time, 'HH:mm')}
                              </span>
                            </div>
                            {daySpan.crossesMidnight && (
                              <Badge
                                bg='dark'
                                className='x-small mt-1'
                                title={`Ca vắt qua nửa đêm, kết thúc ${daySpan.endYmd}. Toàn bộ doanh thu của ca tính cho ngày ${daySpan.startYmd}.`}
                              >
                                Vắt nửa đêm → {daySpan.endYmd}
                              </Badge>
                            )}
                          </td>
                          <td>
                            <div className='small'>{att.checkIn} - {att.checkOut}</div>
                            <div className='small text-muted'>{att.hours}</div>
                          </td>
                          <td>
                            <span className={`small fw-medium ${counts.tasks_completed === counts.tasks_total && counts.tasks_total > 0 ? 'text-success' : ''}`}>
                              {counts.tasks_completed || 0}/{counts.tasks_total || 0}
                            </span>
                          </td>
                          <td className='small'>{counts.orders_total || 0} đơn</td>
                          <td className='text-end small fw-medium'>{formatCurrency(counts.orders_revenue)}</td>
                          <td>
                            <Badge bg={statusCfg.variant}>{statusCfg.label}</Badge>
                            {counts.has_discrepancy && (
                              <Badge bg='danger' className='ms-1' title='Chênh lệch tiền mặt'>
                                <i className='icofont-warning'></i>
                              </Badge>
                            )}
                          </td>
                          <td className='text-end pe-4'>
                            <Button
                              variant='outline-primary'
                              size='sm'
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/shifts/${shift.id}/review`);
                              }}
                            >
                              <i className='icofont-arrow-right'></i>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                {/* Pagination */}
                {meta && (
                  <div className='d-flex justify-content-between align-items-center gap-3 p-3 border-top'>
                    <div className='text-muted small'>
                      Trang {page}/{meta.total_pages || 1} &middot; Tổng {meta.total || 0} ca
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                      <Form.Select
                        size='sm'
                        value={limit}
                        onChange={(e) => {
                          setLimit(Number(e.target.value));
                        }}
                        style={{ width: 'auto' }}
                      >
                        {[10, 20, 50].map((size) => (
                          <option key={size} value={size}>{size}/trang</option>
                        ))}
                      </Form.Select>
                      <Button
                        variant='outline-secondary'
                        size='sm'
                        disabled={page <= 1}
                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      >
                        Trước
                      </Button>
                      <Button
                        variant='outline-secondary'
                        size='sm'
                        disabled={meta.total_pages ? page >= meta.total_pages : true}
                        onClick={() => setPage((prev) => prev + 1)}
                      >
                        Sau
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </PageLayout>
  );
};

export default ShiftReviewListPage;
